import { useEffect, useState } from "react";
import {
  subscribeToBasicPollVotes,
  subscribeToEmbeddedBasicPolls,
  subscribeToMyBasicPollVote,
} from "../../../lib/data/basicPolls";
import {
  hasSubmittedVoteForPoll,
  normalizeVoteOptionIds,
  normalizeVoteRankings,
} from "../../../lib/basic-polls/vote-submission";
import { BASIC_POLL_STATUSES, BASIC_POLL_VOTE_TYPES } from "../../../lib/basic-polls/constants";
import {
  canViewOtherVotesForUser,
  canViewVoterIdentities,
  resolveEmbeddedPollPrivacy,
} from "../../../lib/vote-visibility";

// A Firestore onSnapshot listener is permanently torn down by an error
// callback — it never retries on its own. On a fresh page load the browser
// Firestore SDK can attach a listener a beat before the restored auth token
// propagates, so the first read of the rule-gated votes collection loses the
// race and returns `permission-denied` (and a flaky network/backend can return
// `unavailable`). Without a re-subscribe the embedded poll's "N/M voted" tally
// would then stick at 0 until something else re-triggers the effect. Re-attach
// the listener a few times on these transient codes so the tally self-heals.
const TRANSIENT_VOTE_LISTEN_ERROR_CODES = new Set(["permission-denied", "unavailable"]);
const VOTE_LISTEN_RETRY_LIMIT = 5;
const VOTE_LISTEN_RETRY_DELAY_MS = 400;

export function useSchedulerEmbeddedPollVotes({
  schedulerId,
  userId,
  isCreator = false,
  // Parent scheduler privacy — embedded polls inherit it (clamped to never be
  // less private than the poll's own stored value). Passed as primitives so the
  // subscription effect's deps stay stable.
  schedulerVoteVisibility = null,
  schedulerVoteAnonymization = null,
  schedulerHideVoterIdentities = false,
}) {
  const [embeddedPolls, setEmbeddedPolls] = useState([]);
  const [embeddedPollsLoading, setEmbeddedPollsLoading] = useState(true);
  const [embeddedPollVoteCounts, setEmbeddedPollVoteCounts] = useState({});
  const [embeddedVotesByPoll, setEmbeddedVotesByPoll] = useState({});
  // Per-poll: whether the peer voted/pending STATUS split is known — true only
  // once the gated votes snapshot resolved (viewer is entitled AND the read
  // succeeded). False while loading, when the read is denied/own-only, or on
  // error. Consumers render a neutral split rather than a definitive "0/N voted"
  // while a poll's status is not yet known.
  const [embeddedPollStatusKnown, setEmbeddedPollStatusKnown] = useState({});
  const [embeddedMyVotes, setEmbeddedMyVotes] = useState({});
  const [embeddedVoteDrafts, setEmbeddedVoteDrafts] = useState({});

  useEffect(() => {
    if (!schedulerId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
      setEmbeddedPolls([]);
      setEmbeddedPollsLoading(false);
      return;
    }
    setEmbeddedPollsLoading(true);
    const unsubscribe = subscribeToEmbeddedBasicPolls(
      schedulerId,
      (polls) => {
        setEmbeddedPolls(polls || []);
        setEmbeddedPollsLoading(false);
      },
      () => {
        setEmbeddedPolls([]);
        setEmbeddedPollsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [schedulerId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
    setEmbeddedPollVoteCounts({});
    setEmbeddedVotesByPoll({});
    setEmbeddedPollStatusKnown({});
    if (!schedulerId || embeddedPolls.length === 0) return () => {};

    // Each poll's listener can be re-attached on a transient error; track the
    // currently-active unsubscribe and any pending retry timer per poll so the
    // effect cleanup tears both down without leaking timers or listeners.
    const activeUnsubscribers = new Map();
    const retryTimers = new Map();
    let cancelled = false;

    const subscribePollVotes = (poll, attempt = 0) => {
      const unsubscribe = subscribeToBasicPollVotes(
        "scheduler",
        schedulerId,
        poll.id,
        (voteDocs) => {
          const normalizedVotes = voteDocs || [];
          const count = normalizedVotes.filter((voteDoc) =>
            hasSubmittedVoteForPoll(poll, voteDoc)
          ).length;
          setEmbeddedVotesByPoll((previous) => ({ ...previous, [poll.id]: normalizedVotes }));
          setEmbeddedPollVoteCounts((previous) => {
            if (previous[poll.id] === count) return previous;
            return { ...previous, [poll.id]: count };
          });
          // The gated peer-status snapshot resolved → the per-person split is known.
          setEmbeddedPollStatusKnown((previous) => ({ ...previous, [poll.id]: true }));
        },
        (error) => {
          setEmbeddedVotesByPoll((previous) => ({ ...previous, [poll.id]: [] }));
          setEmbeddedPollVoteCounts((previous) => ({ ...previous, [poll.id]: 0 }));
          setEmbeddedPollStatusKnown((previous) => ({ ...previous, [poll.id]: false }));
          const code = error?.code;
          if (
            cancelled ||
            attempt >= VOTE_LISTEN_RETRY_LIMIT ||
            !TRANSIENT_VOTE_LISTEN_ERROR_CODES.has(code)
          ) {
            return;
          }
          const timer = setTimeout(() => {
            retryTimers.delete(poll.id);
            if (cancelled) return;
            subscribePollVotes(poll, attempt + 1);
          }, VOTE_LISTEN_RETRY_DELAY_MS);
          retryTimers.set(poll.id, timer);
        }
      );
      activeUnsubscribers.set(poll.id, unsubscribe);
    };

    embeddedPolls.forEach((poll) => {
      const myVote = embeddedMyVotes[poll.id] || null;
      const hasVoted = hasSubmittedVoteForPoll(poll, myVote);
      const embeddedPrivacy = resolveEmbeddedPollPrivacy({
        parent: {
          voteVisibility: schedulerVoteVisibility,
          voteAnonymization: schedulerVoteAnonymization,
          hideVoterIdentities: schedulerHideVoterIdentities,
        },
        poll,
      });
      const canReadVoteDetails = canViewOtherVotesForUser({
        voteVisibility: embeddedPrivacy.voteVisibility,
        isCreator,
        hasVoted,
        allParticipantsVoted: poll?.votesAllSubmitted === true,
        isFinalized:
          String(poll?.status || BASIC_POLL_STATUSES.OPEN).toUpperCase() ===
          BASIC_POLL_STATUSES.FINALIZED,
      });
      const canReadVoterIdentities = canViewVoterIdentities({
        isCreator,
        hideVoterIdentities: embeddedPrivacy.hideVoterIdentities,
      });
      // Data-subscription predicate (distinct from the per-target RENDER contract
      // in getParticipantVoteView): can this viewer fetch the full peer votes
      // collection? Same OR-gate as the Firestore rules — if false we subscribe
      // only to the viewer's own vote doc, so the peer voted/pending status is
      // genuinely unknown (statusKnown=false).
      const canReadPeerVoteStatus = canReadVoteDetails || canReadVoterIdentities;

      if (!canReadPeerVoteStatus) {
        const ownVotes = hasVoted && myVote ? [{ id: userId, ...myVote }] : [];
        setEmbeddedVotesByPoll((previous) => ({ ...previous, [poll.id]: ownVotes }));
        setEmbeddedPollVoteCounts((previous) => ({ ...previous, [poll.id]: ownVotes.length }));
        setEmbeddedPollStatusKnown((previous) => ({ ...previous, [poll.id]: false }));
        return;
      }

      subscribePollVotes(poll);
    });

    return () => {
      cancelled = true;
      retryTimers.forEach((timer) => clearTimeout(timer));
      retryTimers.clear();
      activeUnsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") unsubscribe();
      });
      activeUnsubscribers.clear();
    };
  }, [
    embeddedMyVotes,
    embeddedPolls,
    isCreator,
    schedulerId,
    userId,
    schedulerVoteVisibility,
    schedulerVoteAnonymization,
    schedulerHideVoterIdentities,
  ]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
    setEmbeddedMyVotes({});
    if (!schedulerId || !userId || embeddedPolls.length === 0) return () => {};
    const unsubscribers = embeddedPolls.map((poll) =>
      subscribeToMyBasicPollVote(
        "scheduler",
        schedulerId,
        poll.id,
        userId,
        (voteDoc) => {
          setEmbeddedMyVotes((previous) => ({ ...previous, [poll.id]: voteDoc || null }));
        },
        () => {
          setEmbeddedMyVotes((previous) => ({ ...previous, [poll.id]: null }));
        }
      )
    );
    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") unsubscribe();
      });
    };
  }, [embeddedPolls, schedulerId, userId]);

  useEffect(() => {
    if (embeddedPolls.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
      setEmbeddedVoteDrafts({});
      return;
    }
    setEmbeddedVoteDrafts((previous) => {
      const next = { ...previous };
      embeddedPolls.forEach((poll) => {
        const myVote = embeddedMyVotes[poll.id] || null;
        const voteType = poll?.settings?.voteType || BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE;
        if (voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE) {
          next[poll.id] = { rankings: normalizeVoteRankings(myVote) };
          return;
        }
        next[poll.id] = {
          optionIds: normalizeVoteOptionIds(myVote),
          otherText: String(myVote?.otherText || ""),
        };
      });
      return next;
    });
  }, [embeddedMyVotes, embeddedPolls]);

  return {
    embeddedPolls,
    embeddedPollsLoading,
    embeddedPollVoteCounts,
    embeddedPollStatusKnown,
    embeddedVotesByPoll,
    embeddedMyVotes,
    setEmbeddedMyVotes,
    embeddedVoteDrafts,
    setEmbeddedVoteDrafts,
  };
}
